import { useState } from "react";
import { ArrowRight, Check, Copy, Globe, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "./Reveal";

const CONTACT_TEXT = `Rochelle Lee — Loan Officer, NMLS #1623102
Cell: 404-427-0475
Email: RochelleL@prmg.net
Web: rochellel.myprmg.net
115 Westridge Industrial Boulevard, Suite 301, McDonough, GA 30253`;

const ROWS = [
  { icon: Phone, label: "Cell", value: "404-427-0475", href: "tel:4044270475", testId: "officer-phone-link" },
  { icon: Mail, label: "Email", value: "RochelleL@prmg.net", href: "mailto:RochelleL@prmg.net", testId: "officer-email-link" },
  { icon: Globe, label: "Web", value: "rochellel.myprmg.net", href: "https://rochellel.myprmg.net", testId: "officer-web-link" },
  { icon: MapPin, label: "Office", value: "115 Westridge Industrial Blvd, Suite 301, McDonough, GA 30253", testId: "officer-address" },
];

export const Officer = ({ onQualify }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_TEXT);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="contact" data-testid="officer-section" className="bg-[#FAF8F5] py-28 sm:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <div className="relative mx-auto max-w-sm">
            <div className="absolute -inset-3 rotate-[-2deg] rounded-2xl border-2 border-[#F59E0B]" />
            <div className="absolute -inset-3 rotate-[1.5deg] rounded-2xl border border-[#DC2626]/40" />
            <div className="relative overflow-hidden rounded-2xl shadow-[0_30px_80px_rgba(11,22,44,0.3)]">
              <img
                src="/assets/rochelle-lee.jpg"
                alt="Rochelle Lee, Loan Officer"
                data-testid="officer-photo"
                className="aspect-square w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060D1A]/50 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 rounded-full bg-[#060D1A]/80 px-4 py-2 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F59E0B] backdrop-blur-sm">
                NMLS #1623102
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-3">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#D97706]">Chapter 04 — Your Loan Officer</p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#0B162C] sm:text-3xl lg:text-4xl">
            Rochelle Lee
          </h2>
          <p className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Loan Officer · McDonough, Georgia
          </p>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
            Buying or refinancing? Rochelle helps Georgia's heroes claim every dollar they've earned — with straight answers and a
            process built around your schedule.
          </p>
          <div className="mt-8 divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
            {ROWS.map((r) => {
              const inner = (
                <>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0B162C] text-[#F59E0B]">
                    <r.icon size={15} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      {r.label}
                    </span>
                    <span className="block truncate text-sm font-semibold text-[#0B162C] sm:text-base">{r.value}</span>
                  </span>
                </>
              );
              return r.href ? (
                <a
                  key={r.label}
                  href={r.href}
                  data-testid={r.testId}
                  target={r.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-center gap-4 py-4 transition-colors hover:bg-white/60"
                >
                  {inner}
                </a>
              ) : (
                <div key={r.label} data-testid={r.testId} className="flex items-center gap-4 py-4">
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="officer-qualify-btn"
              onClick={onQualify}
              className="group flex items-center gap-3 rounded-full bg-[#DC2626] px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#B91C1C] hover:shadow-[0_14px_40px_rgba(220,38,38,0.45)]"
            >
              Schedule With Rochelle
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="officer-copy-btn"
              onClick={copy}
              className="flex items-center gap-2 rounded-full border border-[#0B162C]/20 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#0B162C] transition-[background-color,border-color] duration-300 hover:border-[#0B162C] hover:bg-white"
            >
              {copied ? <Check size={15} className="text-[#D97706]" /> : <Copy size={15} />}
              {copied ? "Copied" : "Copy Contact Info"}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
